
/**
 * @license undefined
  * Copyright Google Inc. All Rights Reserved.
  * *
  * Use of this source code is governed by an MIT-style license that can be
  * found in the LICENSE file at https://angular.io/license
 * @param {?=} rawParams
 * @return {?}
 */
function paramParser(rawParams: string = ''): Map<string, string[]> {
  const /** @type {?} */ map = new Map<string, string[]>();
  if (rawParams.length > 0) {
    const /** @type {?} */ params: string[] = rawParams.split('&');
    params.forEach((param: string) => {
      const /** @type {?} */ eqIdx = param.indexOf('=');
      const [key, val]: string[] =
          eqIdx == -1 ? [param, ''] : [param.slice(0, eqIdx), param.slice(eqIdx + 1)];
      const /** @type {?} */ list = map.get(key) || [];
      list.push(val);
      map.set(key, list);
    });
  }
  return map;
}
/**
 *  *
 */
export class QueryEncoder {
/**
 * @param {?} k
 * @return {?}
 */
encodeKey(k: string): string { return standardEncoding(k); }
/**
 * @param {?} v
 * @return {?}
 */
encodeValue(v: string): string { return standardEncoding(v); }
}
/**
 * @param {?} v
 * @return {?}
 */
function standardEncoding(v: string): string {
  return encodeURIComponent(v)
      .replace(/%40/gi, '@')
      .replace(/%3A/gi, ':')
      .replace(/%24/gi, '$')
      .replace(/%2C/gi, ',')
      .replace(/%3B/gi, ';')
      .replace(/%2B/gi, '+')
      .replace(/%3D/gi, '=')
      .replace(/%3F/gi, '?')
      .replace(/%2F/gi, '/');
}
/**
 *  Map-like representation of url search parameters, based on
  * [URLSearchParams](https://url.spec.whatwg.org/#urlsearchparams) in the url living standard,
  * with several extensions for merging URLSearchParams objects:
  * - setAll()
  * - appendAll()
  * - replaceAll()
  * *
  * This class accepts an optional second parameter of ${@link QueryEncoder},
  * which is used to serialize parameters before making a request. By default,
  * `QueryEncoder` encodes keys and values of parameters using `encodeURIComponent`,
  * and then un-encodes certain characters that are allowed to be part of the query
  * according to IETF RFC 3986: https://tools.ietf.org/html/rfc3986.
  * *
  * These are the characters that are not encoded: `! $ \' ( ) * + , ; A 9 - . _ ~ ? /`
  * *
  * If the set of allowed query characters is not acceptable for a particular backend,
  * `QueryEncoder` can be subclassed and provided as the 2nd argument to URLSearchParams.
  * *
  * ```
  * import {URLSearchParams, QueryEncoder} from '@angular/http';
  * class MyQueryEncoder extends QueryEncoder {
  * encodeKey(k: string): string {
  * return myEncodingFunction(k);
  * }
  * *
  * encodeValue(v: string): string {
  * return myEncodingFunction(v);
  * }
  * }
  * *
  * let params = new URLSearchParams('', new MyQueryEncoder());
  * ```
 */
export class URLSearchParams {
  paramsMap: Map<string, string[]>;
/**
 * @param {?=} rawParams
 * @param {?=} queryEncoder
 */
constructor(
public rawParams: string = '',
private queryEncoder: QueryEncoder = new QueryEncoder()) {
    this.paramsMap = paramParser(rawParams);
  }
/**
 * @return {?}
 */
clone(): URLSearchParams {
    const /** @type {?} */ clone = new URLSearchParams('', this.queryEncoder);
    clone.appendAll(this);
    return clone;
  }
/**
 * @param {?} param
 * @return {?}
 */
has(param: string): boolean { return this.paramsMap.has(param); }
/**
 * @param {?} param
 * @return {?}
 */
get(param: string): string {
    const /** @type {?} */ storedParam = this.paramsMap.get(param);

    return Array.isArray(storedParam) ? storedParam[0] : null;
  }
/**
 * @param {?} param
 * @return {?}
 */
getAll(param: string): string[] { return this.paramsMap.get(param) || []; }
/**
 * @param {?} param
 * @param {?} val
 * @return {?}
 */
set(param: string, val: string) {
    if (val === void 0 || val === null) {
      this.delete(param);
      return;
    }
    const /** @type {?} */ list = this.paramsMap.get(param) || [];
    list.length = 0;
    list.push(val);
    this.paramsMap.set(param, list);
  }
/**
 * @param {?} searchParams
 * @return {?}
 */
setAll(searchParams: URLSearchParams) {
    searchParams.paramsMap.forEach((value, param) => {
      const /** @type {?} */ list = this.paramsMap.get(param) || [];
      list.length = 0;
      list.push(value[0]);
      this.paramsMap.set(param, list);
    });
  }
/**
 * @param {?} param
 * @param {?} val
 * @return {?}
 */
append(param: string, val: string): void {
    if (val === void 0 || val === null) return;
    const /** @type {?} */ list = this.paramsMap.get(param) || [];
    list.push(val);
    this.paramsMap.set(param, list);
  }
/**
 * @param {?} searchParams
 * @return {?}
 */
appendAll(searchParams: URLSearchParams) {
    searchParams.paramsMap.forEach((value, param) => {
      const /** @type {?} */ list = this.paramsMap.get(param) || [];
      for (let /** @type {?} */ i = 0; i < value.length; ++i) {
        list.push(value[i]);
      }
      this.paramsMap.set(param, list);
    });
  }
/**
 * @param {?} searchParams
 * @return {?}
 */
replaceAll(searchParams: URLSearchParams) {
    searchParams.paramsMap.forEach((value, param) => {
      const /** @type {?} */ list = this.paramsMap.get(param) || [];
      list.length = 0;
      for (let /** @type {?} */ i = 0; i < value.length; ++i) {
        list.push(value[i]);
      }
      this.paramsMap.set(param, list);
    });
  }
/**
 * @return {?}
 */
toString(): string {
    const /** @type {?} */ paramsList: string[] = [];
    this.paramsMap.forEach((values, k) => {
      values.forEach(
          v => paramsList.push(
              this.queryEncoder.encodeKey(k) + '=' + this.queryEncoder.encodeValue(v)));
    });
    return paramsList.join('&');
  }
/**
 * @param {?} param
 * @return {?}
 */
delete (param: string): void { this.paramsMap.delete(param); }
}

function URLSearchParams_tsickle_Closure_declarations() {
 /** @type {?} */
URLSearchParams.prototype.paramsMap;
 /** @type {?} */
URLSearchParams.prototype.rawParams;
 /** @type {?} */
URLSearchParams.prototype.queryEncoder;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
